/**
 * scanDb — offline-first IndexedDB store for the Warehouse Receiving scanner.
 *
 * Two object stores:
 *   - `entries`     : the accumulated session scans (survives reload / offline).
 *   - `retryQueue`  : Option-2 import batches queued while offline, auto-retried
 *                     on reconnect.
 *
 * Pure IndexedDB (no new dependency). Falls back to in-memory if IndexedDB is
 * unavailable (e.g. private-mode quirks) so the UI never hard-fails.
 */

import type { ScanEntry, RetryBatch } from '../types/warehouse';

const DB_NAME = 'warehouse-scan';
const DB_VERSION = 1;
const ENTRIES = 'entries';
const RETRY = 'retryQueue';

let memoryEntries: ScanEntry[] = [];
let memoryRetry: RetryBatch[] = [];
let useMemory = false;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      useMemory = true;
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(ENTRIES)) db.createObjectStore(ENTRIES, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(RETRY)) db.createObjectStore(RETRY, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      useMemory = true;
      reject(req.error ?? new Error('IndexedDB open failed'));
    };
  }).catch((err) => {
    // Allow a later attempt to retry the open instead of caching the rejection.
    dbPromise = null;
    throw err;
  });
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      })
  );
}

// ----- session entries -----

export async function loadEntries(): Promise<ScanEntry[]> {
  try {
    const all = await tx<ScanEntry[]>(ENTRIES, 'readonly', (s) => s.getAll() as IDBRequest<ScanEntry[]>);
    return all.sort((a, b) => a.scannedAt.localeCompare(b.scannedAt));
  } catch {
    return [...memoryEntries];
  }
}

export async function putEntry(entry: ScanEntry): Promise<void> {
  try {
    await tx(ENTRIES, 'readwrite', (s) => s.put(entry));
  } catch {
    memoryEntries = [...memoryEntries.filter((e) => e.id !== entry.id), entry];
  }
}

export async function deleteEntry(id: string): Promise<void> {
  try {
    await tx(ENTRIES, 'readwrite', (s) => s.delete(id));
  } catch {
    memoryEntries = memoryEntries.filter((e) => e.id !== id);
  }
}

export async function clearEntries(): Promise<void> {
  try {
    await tx(ENTRIES, 'readwrite', (s) => s.clear());
  } catch {
    memoryEntries = [];
  }
}

// ----- retry queue -----

export async function loadRetryQueue(): Promise<RetryBatch[]> {
  try {
    return await tx<RetryBatch[]>(RETRY, 'readonly', (s) => s.getAll() as IDBRequest<RetryBatch[]>);
  } catch {
    return [...memoryRetry];
  }
}

export async function enqueueRetry(batch: RetryBatch): Promise<void> {
  try {
    await tx(RETRY, 'readwrite', (s) => s.put(batch));
  } catch {
    memoryRetry = [...memoryRetry.filter((b) => b.id !== batch.id), batch];
  }
}

export async function dequeueRetry(id: string): Promise<void> {
  try {
    await tx(RETRY, 'readwrite', (s) => s.delete(id));
  } catch {
    memoryRetry = memoryRetry.filter((b) => b.id !== id);
  }
}

export const isMemoryFallback = (): boolean => useMemory;

/** Small id generator (no dependency). */
export function localId(): string {
  const rnd = Math.floor(Math.random() * 1e9).toString(36);
  return `${Date.now().toString(36)}-${rnd}`;
}
